import {
  useGetClicksAdvancedReportMutation,
  useGetConversionAdvancedReportMutation,
  useGetCostAdvancedReportMutation,
  useGetOpportunityAdvancedReportMutation,
  useGetRevenueAdvancedReportMutation
} from 'app/apis/advanced-reports/advanced-report-query-api';
import {
  AdvancedReportMetricRowsResult,
  AdvancedReportQueryStatus
} from 'features/advanced-reports/advanced-reports.types';
import { AdvancedReportsQueryCategories } from 'features/advanced-reports/constants/advanced-reports-query-categories';
import { createCompareCacheKey } from 'features/advanced-reports/helpers/cache-helpers';
import { useMemo } from 'react';

export function useGetAdvancedReportResults(
  displayComparisonAttributionModelData: boolean
) {
  const [_getImpressions, primaryImpressionsResult] =
    useGetCostAdvancedReportMutation({
      fixedCacheKey: AdvancedReportsQueryCategories.IMPRESSIONS
    });
  const [_getClicks, primaryClicksResult] = useGetClicksAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.CLICKS
  });
  const [_getCalls, primaryCallResult] = useGetConversionAdvancedReportMutation(
    {
      fixedCacheKey: AdvancedReportsQueryCategories.CONVERSION_INBOUNDCALL
    }
  );
  const [_getWebs, primaryWebResult] = useGetConversionAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.CONVERSION_WEB
  });
  const [_getOpps, primaryOppResult] = useGetOpportunityAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.OPPORTUNITY
  });
  const [_getOppValues, primaryOppValueResult] =
    useGetOpportunityAdvancedReportMutation({
      fixedCacheKey: AdvancedReportsQueryCategories.OPPORTUNITY_VALUE
    });
  const [_getSales, primarySalesResult] = useGetRevenueAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.SALES
  });
  const [_getRevenues, primaryRevenueResult] =
    useGetRevenueAdvancedReportMutation({
      fixedCacheKey: AdvancedReportsQueryCategories.REVENUE
    });
  const [_getAdCosts, primaryCostResult] = useGetCostAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.AD_COST
  });

  const [_getImpressionsCompare, compareImpressionsResult] =
    useGetCostAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.IMPRESSIONS
      )
    });
  const [_getClicksCompare, compareClicksResult] =
    useGetClicksAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.CLICKS
      )
    });
  const [_getCallsCompare, compareCallResult] =
    useGetConversionAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.CONVERSION_INBOUNDCALL
      )
    });
  const [_getWebsCompare, compareWebResult] =
    useGetConversionAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.CONVERSION_WEB
      )
    });
  const [_getOppsCompare, compareOppResult] =
    useGetOpportunityAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.OPPORTUNITY
      )
    });
  const [_getOppValuesCompare, compareOppValueResult] =
    useGetOpportunityAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.OPPORTUNITY_VALUE
      )
    });
  const [_getSalesCompare, compareSalesResult] =
    useGetRevenueAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(AdvancedReportsQueryCategories.SALES)
    });
  const [_getRevenuesCompare, compareRevenueResult] =
    useGetRevenueAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.REVENUE
      )
    });
  const [_getAdCostsCompare, compareCostResult] =
    useGetCostAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.AD_COST
      )
    });

  return useMemo(() => {
    const primaryRowsResult: AdvancedReportMetricRowsResult = {
      impressions: primaryImpressionsResult.data ?? [],
      clicks: primaryClicksResult.data ?? [],
      calls: primaryCallResult.data ?? [],
      webs: primaryWebResult.data ?? [],
      opps: primaryOppResult.data ?? [],
      oppValues: primaryOppValueResult.data ?? [],
      sales: primarySalesResult.data ?? [],
      revenues: primaryRevenueResult.data ?? [],
      costs: primaryCostResult.data ?? []
    };
    const primaryQueryStatus: AdvancedReportQueryStatus = {
      impressions: primaryImpressionsResult.status,
      clicks: primaryClicksResult.status,
      calls: primaryCallResult.status,
      webs: primaryWebResult.status,
      opps: primaryOppResult.status,
      oppValues: primaryOppValueResult.status,
      sales: primarySalesResult.status,
      revenues: primaryRevenueResult.status,
      costs: primaryCostResult.status
    };

    const compareRowsResult: AdvancedReportMetricRowsResult = {
      impressions:
        compareImpressionsResult.data && displayComparisonAttributionModelData
          ? compareImpressionsResult.data
          : [],
      clicks:
        compareClicksResult.data && displayComparisonAttributionModelData
          ? compareClicksResult.data
          : [],
      calls:
        compareCallResult.data && displayComparisonAttributionModelData
          ? compareCallResult.data
          : [],
      webs:
        compareWebResult.data && displayComparisonAttributionModelData
          ? compareWebResult.data
          : [],
      opps:
        compareOppResult.data && displayComparisonAttributionModelData
          ? compareOppResult.data
          : [],
      oppValues:
        compareOppValueResult.data && displayComparisonAttributionModelData
          ? compareOppValueResult.data
          : [],
      sales:
        compareSalesResult.data && displayComparisonAttributionModelData
          ? compareSalesResult.data
          : [],
      revenues:
        compareRevenueResult.data && displayComparisonAttributionModelData
          ? compareRevenueResult.data
          : [],
      costs:
        compareCostResult.data && displayComparisonAttributionModelData
          ? compareCostResult.data
          : []
    };
    const compareQueryStatus: AdvancedReportQueryStatus = {
      impressions: compareImpressionsResult.status,
      clicks: compareClicksResult.status,
      calls: compareCallResult.status,
      webs: compareWebResult.status,
      opps: compareOppResult.status,
      oppValues: compareOppValueResult.status,
      sales: compareSalesResult.status,
      revenues: compareRevenueResult.status,
      costs: compareCostResult.status
    };

    return {
      primaryRowsResult,
      primaryQueryStatus,
      compareRowsResult,
      compareQueryStatus
    };
  }, [
    primaryImpressionsResult,
    primaryClicksResult,
    primaryCallResult,
    primaryWebResult,
    primaryOppResult,
    primaryOppValueResult,
    primarySalesResult,
    primaryRevenueResult,
    primaryCostResult,
    compareImpressionsResult,
    compareClicksResult,
    compareCallResult,
    compareWebResult,
    compareOppResult,
    compareOppValueResult,
    compareSalesResult,
    compareRevenueResult,
    compareCostResult,
    displayComparisonAttributionModelData
  ]);
}
